import { useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';

/**
 * Custom hook to detect user inactivity
 * @param {Function} onIdle
 * @param {Function} onWarn
 * @param {number|string} idleTime
 * @param {number|string} warningTime 
 */
export const useIdleTimer = ({
  onIdle,
  onWarn,
  idleTime: manualIdleTime,
  warningTime: manualWarningTime
}) => {
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const onIdleRef = useRef(onIdle);
  const onWarnRef = useRef(onWarn);

  useEffect(() => {
    onIdleRef.current = onIdle;
    onWarnRef.current = onWarn;
  }, [onIdle, onWarn]);

  const parseTime = useCallback((t) => {
    if (typeof t === 'number') return t;
    if (typeof t !== 'string') return 0;
    try {
      // eslint-disable-next-line no-new-func
      return Function(`'use strict'; return (${t})`)();
    } catch {
      const parsed = parseInt(t, 10);
      return isNaN(parsed) ? 0 : parsed;
    }
  }, []);

  const idleTime = parseTime(manualIdleTime) || parseTime(process.env.REACT_APP_IDLE_TIME) || (10 * 60 * 1000);
  const warningTime = parseTime(manualWarningTime) || (30 * 1000);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearTimers();

    // Set warning timer (only if wait time is positive)
    const warnDelay = idleTime - warningTime;
    if (onWarnRef.current && warnDelay > 0) {
      warningTimerRef.current = setTimeout(() => {
        if (onWarnRef.current) onWarnRef.current();
      }, warnDelay);
    }

    // Set idle timer
    if (idleTime > 0) {
      idleTimerRef.current = setTimeout(() => {
        if (onIdleRef.current) onIdleRef.current();
      }, idleTime);
    }
  }, [idleTime, warningTime, clearTimers]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      toast.dismiss('idle-warning');
      resetTimer();
    };

    events.forEach(event => window.addEventListener(event, handleActivity));

    resetTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      clearTimers();
    };
  }, [resetTimer, clearTimers]);

  return { resetTimer };
};
